import { X } from "lucide-react";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import {
  CHAT_MESSAGE_COUNTER_THRESHOLD,
  MAX_CHAT_MESSAGE_LENGTH,
} from "@/stores/chat/messageLimits";
import type { ChatSendError } from "./useChatSend";

function localeOf(language: string): string {
  return language === "et" ? "et-EE" : "en-GB";
}

/**
 * The line under Franklin's composer: the character counter once the draft
 * nears the limit, why Send is disabled once it is over, and any send that was
 * refused before it reached the thread (which therefore has nothing to show).
 *
 * The limit is shown, never enforced with `maxLength`: a hard cap would
 * silently cut a long paste short, and the user would send half of it.
 */
export function ComposerStatusLine({
  id,
  length,
  sendError,
  onDismissError,
}: {
  /** Referenced by the field and the Send button through `aria-describedby`. */
  id: string;
  /** Length of the draft as the limit counts it (`chatMessageLength`). */
  length: number;
  sendError?: ChatSendError | null;
  onDismissError?: () => void;
}) {
  const { t, language } = useLanguage();
  const copy = t.documents.chatSidebar;
  const format = new Intl.NumberFormat(localeOf(language));
  const values = { count: format.format(length), limit: format.format(MAX_CHAT_MESSAGE_LENGTH) };

  const overLimit = length > MAX_CHAT_MESSAGE_LENGTH;
  const showCounter = length >= CHAT_MESSAGE_COUNTER_THRESHOLD;
  // Over the limit, the draft's own state is the thing to act on — it also
  // covers a `tooLong` refusal, whose text is now this over-long draft.
  const message = overLimit
    ? fill(copy.composerLimit.overLimit, values)
    : sendError
      ? fill(copy.sendFailed[sendError], values)
      : null;

  if (!message && !showCounter) return null;

  return (
    <div
      id={id}
      data-testid="composer-status"
      className="mt-1.5 flex items-start gap-2 px-2 text-[11px] leading-4"
    >
      {/* Mounted only while there is something to say, so `alert` announces
          it once when it appears rather than on every keystroke. */}
      <div className="flex min-w-0 flex-1 items-start gap-1">
        {message && (
          <p role="alert" className="min-w-0 flex-1 text-destructive">
            {message}
          </p>
        )}
        {!overLimit && sendError && onDismissError && (
          <button
            type="button"
            onClick={onDismissError}
            aria-label={copy.sendFailed.dismiss}
            className="shrink-0 rounded p-0.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      {showCounter && (
        <span
          data-testid="composer-character-count"
          className={cn(
            "shrink-0 tabular-nums",
            overLimit ? "font-medium text-destructive" : "text-neutral-500"
          )}
        >
          <span aria-hidden="true">{fill(copy.composerLimit.counter, values)}</span>
          <span className="sr-only">{fill(copy.composerLimit.counterLabel, values)}</span>
        </span>
      )}
    </div>
  );
}
